import { Suspense } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { textAreaDefaultMaxLength } from "@/models/common"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { DiagnosisCode } from "@/modules/patients/components/diagnosis-code"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { useLatestDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useLatestDiagnosisCode"
import { REASONS } from "@/modules/patients/detail/therapy-prescription/ch/constants"
import {
  PsychologicalPsychotherapy,
  PsychologicalPsychotherapySchema,
} from "@/modules/patients/detail/therapy-prescription/ch/schema/psychological-psychotherapy-schema"
import { TherapyPrescription } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/useChCreatePsychologicalPsychoTherapyMutation.graphql"
import { chPsychologicalPsychoTherapyFragment } from "@/modules/patients/detail/therapy-prescription/ch/services/chPsychologicalPsychoTherapyFragment"
import { useChCreatePsychologicalPsychoTherapyMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChCreatePsychologicalPsychoTherapyMutation"
import { useChDeletePsychologicalPsychoTherapyMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChDeletePsychologicalPsychoTherapyMutation"
import { useChGeneratePsychotherapyDocumentMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChGeneratePsychotherapyDocumentMutation"
import { useChUpdatePsychologicalPsychoTherapyMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChUpdatePsychologicalPsychoTherapyMutation"
import { usePsychologicalPsytherapyStoreContext } from "@/modules/patients/detail/therapy-prescription/ch/store/usePsychologicalPsytherapyStore"
import {
  DOCUMENT_LANGUAGES,
  getDocumentLanguageLabel,
  useDefaultDocumentLanguage,
} from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

const THERAPY_PRESCRIPTIONS: Array<TherapyPrescription> = [
  "CRISIS_INTERVENTION_SHORT_THERAPY",
  "PSYCHOTHERAPY_PRESCRIPTION1",
  "PSYCHOTHERAPY_PRESCRIPTION2",
  "TREATMENT",
]

interface ChPsychologicalTherapyDrawerProps extends FetchKeyProps {
  progressNoteId?: string
}

const ChPsychologicalTherapyDrawer = withErrorBoundary(
  ({ progressNoteId, fetchKey }: ChPsychologicalTherapyDrawerProps) => {
    const {
      t,
      i18n: { exists },
    } = useTranslation()
    const user = useUser()
    const hasSavePermission = user.isOneOf(["Doctor", "MedicalSupervisor"])
    const { toast } = useToast()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const [createPsychologicalPsychoTherapy, isCreating] = useChCreatePsychologicalPsychoTherapyMutation()
    const [updatePsychologicalPsychoTherapy, isUpdating] = useChUpdatePsychologicalPsychoTherapyMutation()
    const [deletePsychologicalPsychoTherapy] = useChDeletePsychologicalPsychoTherapyMutation()
    const [generateDocument, isGenerating] = useChGeneratePsychotherapyDocumentMutation()
    const patientId = usePatientParam()
    const psychologicalPsychotherapyId = usePsychologicalPsytherapyStoreContext(
      (state) => state.psychologicalPsychotherapyId,
    )
    const psychologicalPsytherapyRef = usePsychologicalPsytherapyStoreContext(
      (state) => state.psychologicalPsytherapyRef,
    )
    const setPsychologicalPsytherapy = usePsychologicalPsytherapyStoreContext(
      (state) => state.setPsychologicalPsytherapy,
    )
    const isEditMode = !!psychologicalPsychotherapyId
    const isAddMode = !isEditMode && patientId && progressNoteId
    const persistedData = useFragment(chPsychologicalPsychoTherapyFragment, psychologicalPsytherapyRef)
    const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy

    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)
    const includeEnglishDocument = true
    const { documentLanguage } = useDefaultDocumentLanguage()
    const { diagnosisCode: latestDiagnosisCode } = useLatestDiagnosisCode(progressNoteId)

    const parsedData = isEditMode
      ? PsychologicalPsychotherapySchema.parse({
          ...persistedData,
          additionalInformation: persistedData?.additionalInformation ?? "",
          healthcareProviderId: persistedData?.healthcareProviderId ?? "",
        })
      : null

    const form = useForm<PsychologicalPsychotherapy>({
      mode: "all",
      resolver: zodResolver(PsychologicalPsychotherapySchema),
      defaultValues:
        isEditMode && parsedData
          ? {
              ...parsedData,
            }
          : {
              language: documentLanguage,
              therapyPrescription: undefined,
              reason: "SICKNESS",
              diagnosisCode: latestDiagnosisCode,
              additionalInformation: "",
            },
    })

    const {
      register,
      control,
      formState: { errors, isDirty },
    } = form

    useDirtyDrawerMaker(isDirty)

    const handleGeneratingDocument = (id: string, diagnosisCode: string) => {
      if (!progressNoteId) return

      generateDocument({
        id,
        diagnosisCode,
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }

    const handleCloseDrawer = () => {
      setPsychologicalPsytherapy(null, null)
      closeDrawer()
      FaroInsightsWrapper.pushEvent("documents_psychotherapy_close")
    }

    const onSubmit: SubmitHandler<PsychologicalPsychotherapy> = (data) => {
      if (!progressNoteId) return

      const formattedData = {
        ...data,
        additionalInformation:
          data.additionalInformation && data.additionalInformation.length > 0 ? data.additionalInformation : null,
        healthcareProviderId: data.healthcareProviderId || null,
      }

      if (isAddMode) {
        createPsychologicalPsychoTherapy({
          variables: {
            input: {
              additionalInformation: formattedData.additionalInformation,
              healthcareProviderId: formattedData.healthcareProviderId,
              language: formattedData.language,
              reason: formattedData.reason,
              therapyPrescription: formattedData.therapyPrescription,
              patientId,
              progressNoteId,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const id = response.ch_createPsychotherapyPrescription.ch_PsychotherapyPrescription?.id
            if (id) {
              handleGeneratingDocument(id, formattedData.diagnosisCode)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const psychotherapyPrescription = data?.ch_createPsychotherapyPrescription?.ch_PsychotherapyPrescription

              if (psychotherapyPrescription?.id) {
                const newRecordProxy = store.get(psychotherapyPrescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_psychotherapyPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode) {
        updatePsychologicalPsychoTherapy({
          variables: {
            input: {
              additionalInformation: formattedData.additionalInformation,
              healthcareProviderId: formattedData.healthcareProviderId,
              language: formattedData.language,
              reason: formattedData.reason,
              therapyPrescription: formattedData.therapyPrescription,
              id: psychologicalPsychotherapyId,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const id = response.ch_updatePsychotherapyPrescription.ch_PsychotherapyPrescription?.id
            if (id) {
              handleGeneratingDocument(id, formattedData.diagnosisCode)
            }
          },
        })
      }
    }

    const onDelete = () => {
      if (progressNoteId && psychologicalPsychotherapyId) {
        deletePsychologicalPsychoTherapy({
          variables: {
            input: {
              id: psychologicalPsychotherapyId,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.psychologicalPsychotherapy.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.psychologicalPsychotherapy.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
      })
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="psychological-psychotherapy-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("patient.therapyPrescription.therapyPrescriptionPsychologicalPsychotherapy")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="psychological-psychotherapy-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({
                    field: { name, onChange, value, ref },
                    formState: { errors },
                    fieldState: { invalid },
                  }) => (
                    <div>
                      <Select value={value} onValueChange={onChange} {...ref}>
                        <SelectTrigger hasError={invalid} className="w-56.5">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.therapyPrescription.documentLanguage")}
                              value={t(getDocumentLanguageLabel(value, includeEnglishDocument))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>
              <div className="mb-8 flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.to")}</label>
                <div className="w-full">
                  <FormField
                    name="healthcareProviderId"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <CareProviderSearch
                        label={t("patient.therapyPrescription.healthcareProvider")}
                        searchType="THERAPY_PRESCRIPTION_PSYCHOLOGICAL_PSYCHOTHERAPY_DOCUMENT"
                        name={name}
                        value={value}
                        analyticsSectionName="documents_psychotherapy"
                        defaultAdvancedSearchFilters={{ groupType: "PSYCHOTHERAPIST" }}
                        onSelect={onChange}
                        onRemove={() => onChange("")}
                        error={errors.healthcareProviderId?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.for")}</label>
                <div className="flex">
                  <FormField
                    control={control}
                    name="therapyPrescription"
                    render={({
                      field: { name, onChange, value, ref },
                      formState: { errors },
                      fieldState: { invalid },
                    }) => (
                      <div className="w-full" data-testid="therapy-prescription-input">
                        <Select onValueChange={onChange} {...ref} defaultValue={value}>
                          <SelectTrigger hasError={invalid}>
                            <SelectValue
                              asChild
                              placeholder={t("patient.therapyPrescription.therapyPrescription.default") + "*"}>
                              <LabelAndSelectValue
                                data-testid="selected-therapy-prescription-type"
                                label={t("patient.therapyPrescription.therapyPrescription.default") + "*"}
                                value={
                                  value ? t(`patient.therapyPrescription.therapyPrescriptions.${value}.title`) : ""
                                }
                              />
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {THERAPY_PRESCRIPTIONS.map((therapyPrescription) => {
                              const title = t(
                                `patient.therapyPrescription.therapyPrescriptions.${therapyPrescription}.title`,
                              )

                              const subTitleExists = exists(
                                `patient.therapyPrescription.therapyPrescriptions.${therapyPrescription}.subtitle`,
                              )
                              const subTitle = t(
                                `patient.therapyPrescription.therapyPrescriptions.${therapyPrescription}.subtitle`,
                              )

                              return (
                                <SelectItem
                                  key={therapyPrescription}
                                  value={therapyPrescription}
                                  data-testid={therapyPrescription.toLocaleLowerCase()}>
                                  <p>{title}</p>
                                  {subTitleExists && subTitle && (
                                    <p className="text-on-surface-subdued truncate text-xs">{subTitle}</p>
                                  )}
                                </SelectItem>
                              )
                            })}
                          </SelectContent>
                        </Select>
                        <TranslatedFieldError errors={errors} name={name} />
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4 pt-8">
                <label className="text-on-surface text-base font-medium">
                  {t("patient.therapyPrescription.reason")}
                </label>
                <div className="flex gap-4">
                  <FormField
                    control={control}
                    name="reason"
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        className="flex flex-wrap"
                        onValueChange={onChange}
                        defaultValue={value}
                        value={value}>
                        {REASONS.map((reason) => (
                          <RadioGroupItem
                            key={reason}
                            value={reason}
                            data-testid={`radio-item-${reason.toLowerCase()}`}>
                            <RadioGroupLabel>{t(`patient.therapyPrescription.reasons.${reason}`)}</RadioGroupLabel>
                          </RadioGroupItem>
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name="diagnosisCode"
                  render={({ field: { name, value, onChange }, fieldState: { invalid } }) => (
                    <Suspense fallback={null}>
                      <DiagnosisCode
                        defaultValue={isAddMode ? latestDiagnosisCode : persistedData?.diagnosisCode}
                        name={name}
                        errors={errors}
                        invalid={invalid}
                        value={value}
                        fetchKey={fetchKey}
                        onSelect={onChange}
                      />
                    </Suspense>
                  )}
                />
                <TextArea
                  {...register("additionalInformation")}
                  data-testid="additional-information-input"
                  error={errors.additionalInformation?.message}
                  translateError={true}
                  label={t("patient.therapyPrescription.additionalInfo")}
                  textLimit={textAreaDefaultMaxLength}
                />
              </div>
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="psychological-psychotherapy-footer">
              {isEditMode && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                  <button
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_psychotherapy_delete")
                      handleDelete()
                    }}
                    data-testid="psychological-psychotherapy-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  data-testid="psychological-psychotherapy-cancel-button"
                  variant="secondary"
                  onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  disabled={!hasSavePermission || (isEditMode && !isDirty)}
                  data-testid="psychological-psychotherapy-save-button"
                  isLoading={isCreating || isUpdating || isGenerating}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_psychotherapy_save")
                    form.handleSubmit(onSubmit)()
                  }}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChPsychologicalTherapyDrawer",
)

export { ChPsychologicalTherapyDrawer }
